import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Heading,
  Divider,
  SimpleGrid,
  Textarea,
  useColorModeValue,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
  Text,
  Select,
  useToast,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { getData, postData } from '../../utils/httpClient.js';
import PageTable from 'components/layout/PageTable';

export default function CreateProviderPayment() {
  const [form, setForm] = useState({
    proveedor_id: '',
    fecha_pago: '',
    metodo_pago: 1, // 1: Efectivo, 2: Cheque, 3: Transferencia
    referencia: '',
    observaciones: '',
    facturas_seleccionadas: [],
    monto_total: 0,
  });

  const [providers, setProviders] = useState([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [providerSearch, setProviderSearch] = useState('');
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [showProviderSuggestions, setShowProviderSuggestions] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    loadProviders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProviders = async () => {
    try {
      const response = await getData('/providers');
      setProviders(response?.payload?.providers || []);
      console.log('Proveedores cargados:', providers);
    } catch (error) {
      console.log('Error loading providers:', error);
      toast({
        title: 'Error al cargar proveedores',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadPurchaseInvoices = async (providerId) => {
    try {
      const response = await getData(`/provider-invoices/provider/${providerId}?status=Pendiente`);
      console.log('Facturas pendientes cargadas:', response?.payload?.invoices);
      setPurchaseInvoices(response?.payload?.invoices || []);
    } catch (error) {
      toast({
        title: 'Error al cargar facturas pendientes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleProviderSearch = (value) => {
    setProviderSearch(value);
    if (value.trim()) {
      const filtered = providers.filter(provider =>
        provider.name?.toLowerCase().includes(value.toLowerCase()) ||
        provider.rut?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProviders(filtered);
      setShowProviderSuggestions(true);
    } else {
      setShowProviderSuggestions(false);
    }
  };

  const selectProvider = (provider) => {
    setForm(prev => ({
      ...prev,
      proveedor_id: provider.id,
      proveedor: provider
    }));
    setProviderSearch(`${provider.name} - ${provider.rut}`);
    setShowProviderSuggestions(false);
    loadPurchaseInvoices(provider.id);
    setSelectedInvoices([]);
    setManualAmount('');
  };

  const toggleInvoiceSelection = (invoice) => {
    const invoiceKey = invoice.invoiceId;
    setSelectedInvoices((prev) => {
      const exists = prev.some((inv) => inv.invoiceId === invoiceKey);
      if (exists) {
        return prev.filter((inv) => inv.invoiceId !== invoiceKey);
      }
      return [...prev, invoice];
    });
  };

  useEffect(() => {
    if (selectedInvoices.length > 0) {
      const total = selectedInvoices.reduce(
        (sum, invoice) => sum + (Number(invoice.totalAmount) || 0),
        0,
      );
      setForm((prev) => ({
        ...prev,
        monto_total: total,
        facturas_seleccionadas: selectedInvoices.map((inv) => inv.invoiceId),
      }));
    } else {
      const parsed = parseFloat(String(manualAmount).replace(',', '.'));
      setForm((prev) => ({
        ...prev,
        monto_total: Number.isFinite(parsed) ? parsed : 0,
        facturas_seleccionadas: [],
      }));
    }
  }, [selectedInvoices, manualAmount]);

  const getEstadoColor = (estado) => {
    switch (String(estado || '').toLowerCase()) {
      case 'pendiente':
        return 'yellow';
      case 'pagada':
      case 'pagado':
        return 'green';
      case 'vencida':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-UY');
  };

  const handleSubmit = async () => {
    const amount =
      selectedInvoices.length > 0
        ? form.monto_total
        : parseFloat(String(manualAmount).replace(',', '.'));

    if (!form.proveedor_id || !form.fecha_pago) {
      toast({
        title: 'Campos requeridos',
        description: 'Indique proveedor y fecha de pago',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      toast({
        title: 'Monto inválido',
        description:
          'Ingrese un monto mayor a cero o seleccione facturas pendientes para calcular el total',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        providerId: form.proveedor_id,
        paymentDate: form.fecha_pago,
        paymentType: Number(form.metodo_pago),
        paymentReference: form.referencia,
        observations: form.observaciones,
        paymentAmount: amount,
        providerInvoices: selectedInvoices.map((invoice) => ({
          invoiceId: invoice.invoiceId,
          totalAmount: invoice.totalAmount,
        })),
      };

      await postData('/provider-payments', payload);

      toast({
        title: 'Pago registrado exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setForm({
        proveedor_id: '',
        fecha_pago: '',
        metodo_pago: 1,
        referencia: '',
        observaciones: '',
        facturas_seleccionadas: [],
        monto_total: 0,
      });
      setProviderSearch('');
      setPurchaseInvoices([]);
      setSelectedInvoices([]);
      setManualAmount('');
    } catch (error) {
      toast({
        title: 'Error al registrar pago',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTable flexDirection="column" w="100%" px={{ base: 3, md: 4 }} py={{ base: 3, md: 4 }} stickyActions={false}>
      <VStack spacing={4} align="stretch">
        <Heading size="md" color="secondaryGray.900">
          Registrar Pago a Proveedor
        </Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Proveedor</FormLabel>
                <InputGroup>
                  <Input
                    size="sm"
                    value={providerSearch}
                    onChange={(e) => handleProviderSearch(e.target.value)}
                    placeholder="Buscar proveedor por nombre o RUT"
                  />
                  <InputRightElement>
                    <SearchIcon />
                  </InputRightElement>
                </InputGroup>
                
                {showProviderSuggestions && filteredProviders.length > 0 && (
                  <Box
                    position="relative"
                    bg={bgColor}
                    border="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    mt={1}
                    maxH="200px"
                    overflowY="auto"
                    zIndex={10}
                  >
                    <List>
                      {filteredProviders.slice(0, 10).map((provider) => (
                        <ListItem
                          key={provider.id}
                          p={2}
                          cursor="pointer"
                          _hover={{ bg: 'gray.100' }}
                          onClick={() => selectProvider(provider)}
                        >
                          <Text fontSize="sm" fontWeight="medium" color="blue.600">
                            {provider.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            RUT: {provider.rut}
                          </Text>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Fecha de Pago</FormLabel>
                <Input size="sm" type="date"
                  value={form.fecha_pago}
                  onChange={(e) => setForm(prev => ({ ...prev, fecha_pago: e.target.value }))}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Método de Pago</FormLabel>
                <Select
                  value={form.metodo_pago}
                  onChange={(e) => setForm(prev => ({ ...prev, metodo_pago: e.target.value }))}
                >
                  <option value="3">Transferencia Bancaria</option>
                  <option value="2">Cheque</option>
                  <option value="1">Efectivo</option>
                  <option value="2">Débito Automático</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Referencia/Comprobante</FormLabel>
                <Input
                  value={form.referencia}
                  onChange={(e) => setForm(prev => ({ ...prev, referencia: e.target.value }))}
                  placeholder="Número de comprobante, cheque, etc."
                />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>Observaciones</FormLabel>
              <Textarea
                value={form.observaciones}
                onChange={(e) => setForm(prev => ({ ...prev, observaciones: e.target.value }))}
                placeholder="Observaciones adicionales del pago"
              />
            </FormControl>

            <Divider />

            <FormControl isRequired={selectedInvoices.length === 0}>
              <FormLabel>Monto del pago</FormLabel>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={
                  selectedInvoices.length > 0
                    ? String(form.monto_total ?? '')
                    : manualAmount
                }
                isReadOnly={selectedInvoices.length > 0}
                onChange={(e) => setManualAmount(e.target.value)}
                placeholder={
                  selectedInvoices.length > 0
                    ? 'Calculado desde facturas seleccionadas'
                    : 'Monto sin factura asociada'
                }
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Opcional: asocie una o más facturas pendientes abajo; si no, ingrese el monto manualmente.
              </Text>
            </FormControl>

            {form.proveedor_id && (
              <VStack spacing={4} align="stretch">
                <Heading size="md">Facturas pendientes (opcional)</Heading>
                
                {purchaseInvoices.length > 0 ? (
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Seleccionar</Th>
                          <Th>Número</Th>
                          <Th>Fecha</Th>
                          <Th>Vencimiento</Th>
                          <Th>Total</Th>
                          <Th>Estado</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {purchaseInvoices.map((invoice) => (
                          <Tr key={invoice.invoiceId}>
                            <Td>
                              <Checkbox
                                isChecked={selectedInvoices.some(
                                  (inv) => inv.invoiceId === invoice.invoiceId,
                                )}
                                onChange={() => toggleInvoiceSelection(invoice)}
                              />
                            </Td>
                            <Td>{invoice.invoiceId}</Td>
                            <Td>{formatDate(invoice.date)}</Td>
                            <Td>{formatDate(invoice.expirationDate)}</Td>
                            <Td>
                              ${Number(invoice.totalAmount || 0).toFixed(2)}
                            </Td>
                            <Td>
                              <Badge colorScheme={getEstadoColor(invoice.status)}>
                                {invoice.status}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Text color="gray.500" textAlign="center">
                    No hay facturas pendientes para este proveedor
                  </Text>
                )}
              </VStack>
            )}

            {selectedInvoices.length > 0 && (
              <>
                <Divider />
                <HStack justify="space-between" align="center">
                  <Text fontSize="lg" color="secondaryGray.600">
                    Facturas seleccionadas: <Text as="span" fontWeight="medium" color="secondaryGray.900">{selectedInvoices.length}</Text>
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="green.600">
                    Total a pagar: ${form.monto_total?.toFixed(2) || '0.00'}
                  </Text>
                </HStack>
              </>
            )}

            <HStack justify="end" spacing={4}>
              <Button
                colorScheme="gray"
                onClick={handleSubmit}
                isLoading={loading}
                loadingText="Registrando pago..."
                isDisabled={!form.proveedor_id || !form.fecha_pago}
              >
                Registrar Pago
              </Button>
            </HStack>
          </VStack>
    </PageTable>
  );
}
